import { injectable } from 'tsyringe';
import type { PermissionEntry } from '@bmai/auth-api-client';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { PermissionsAdminService } from './PermissionsAdminService';

interface ResourceGroup {
    resource: string;
    actions: string[];
}

@injectable()
export class PermissionsTabViewModel extends BaseViewModel {
    perms: PermissionEntry[] | null = null;
    loading: boolean = true;
    search: string = '';

    constructor(private service: PermissionsAdminService) {
        super();
    }

    get resourceGroups(): ResourceGroup[] {
        const byResource = new Map<string, Set<string>>();
        for (const p of this.perms ?? []) {
            const set = byResource.get(p.resource) ?? new Set<string>();
            set.add(p.action);
            byResource.set(p.resource, set);
        }
        return [...byResource.entries()]
            .map(([resource, actions]) => ({ resource, actions: [...actions].sort() }))
            .sort((a, b) => a.resource.localeCompare(b.resource));
    }

    /** {@link resourceGroups} filtered by the search box (resource or action match). */
    get filteredGroups(): ResourceGroup[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.resourceGroups;
        return this.resourceGroups.filter(
            (g) => g.resource.toLowerCase().includes(q) || g.actions.some((a) => a.toLowerCase().includes(q)),
        );
    }

    /** Total registered (resource, action) permissions. */
    get totalPermissions(): number {
        return this.perms?.length ?? 0;
    }

    setSearch(value: string): void {
        this.search = value;
        this.notifyChange();
    }

    async load(): Promise<void> {
        this.loading = true;
        this.notifyChange();
        try {
            this.perms = await this.service.listPermissions();
        } catch {
            this.perms = null;
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

}
